/* eslint-disable @typescript-eslint/no-explicit-any */

import type { MapRef } from 'react-map-gl/mapbox';
import { ReloadType } from '@/app/components/search/utils';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { getCoordinatesFromFeatureCollection } from '@/utils/mapUtils.ts';
import type { SearchInfoDetailData } from '@/app/search/types.ts';
import { DESTINATION_TYPE } from '@/app/search/types.ts';
import type { Feature, FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import { decoratePoints } from './decoratePoints';
import type { DealCard } from '../../utils/buildDealCards/dealCardTypes';
import type { ItineraryStepProduct } from '../../utils/buildDealCards/MapProductsResponse';

const addCitiesCoordinatesToBound = async ({
  searchInfoDetailData,
}: {
  searchInfoDetailData: SearchInfoDetailData;
}): Promise<FeatureCollection> => {
  const points: Feature<Point, GeoJsonProperties>[] = [];
  if (searchInfoDetailData.destination?.coordinates) {
    // @ts-ignore
    searchInfoDetailData.destination?.coordinates.forEach((c) => {
      if (!c.lat || !c.lng) return;
      points.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [c.lng, c.lat],
        },
      });
    });
  }

  return {
    type: 'FeatureCollection',
    features: points,
  };
};

const adjustMapPositionUp = (map: any, win = window) => {
  const winWidth = win.innerWidth;
  const winHeight = win.innerHeight;
  map.fitScreenCoordinates([0, 0], [winWidth, (winHeight / 3) * 4], 0);
};

type HandleMapLoad = {
  searchInfoDetailData: SearchInfoDetailData;
  reloadType: ReloadType;
  mapRef: React.RefObject<MapRef | null>;
  searchParams: SearchParams;
  dealCards?: DealCard[];
  isSearch?: boolean;
  isFilter?: boolean;
  isPaging?: boolean;
  coordinates?: { lat: number; lon: number };
  setIsResizing?: React.Dispatch<React.SetStateAction<boolean>>;
  itinerary?: ItineraryStepProduct[];
  isMobile: boolean;
};
const handleMapLoad = async ({
  searchInfoDetailData,
  reloadType,
  mapRef,
  searchParams,
  dealCards,
  coordinates,
  setIsResizing,
  isMobile,
}: HandleMapLoad): Promise<void> => {
  if (!mapRef.current) return;
  if (coordinates && setIsResizing) {
    setIsResizing(true);
  }

  const points = decoratePoints(dealCards);
  const isMapSearch: boolean = !!searchParams[URLParameters.MAP_AREA];
  const isTag = searchInfoDetailData.destination.destinationType === DESTINATION_TYPE.TAG;
  let outputCoordinates = {
    minLng: 0,
    minLat: 0,
    maxLng: 0,
    maxLat: 0,
  };

  if (isMapSearch) {
    const mapAreaCoordinates = searchParams[URLParameters.MAP_AREA].split(',');
    outputCoordinates = {
      minLng: parseFloat(mapAreaCoordinates[2]),
      minLat: parseFloat(mapAreaCoordinates[3]),
      maxLng: parseFloat(mapAreaCoordinates[4]),
      maxLat: parseFloat(mapAreaCoordinates[1]),
    };
  } else {
    if ((points && points.length > 0) || searchInfoDetailData.destination.destinationType === DESTINATION_TYPE.TAG) {
      const featureCollection: FeatureCollection =
        points.length === 0
          ? await addCitiesCoordinatesToBound({ searchInfoDetailData })
          : {
              type: 'FeatureCollection',
              features: points,
            };
      outputCoordinates = getCoordinatesFromFeatureCollection(featureCollection);
    }

    const { minLng, minLat, maxLng, maxLat } = outputCoordinates;
    if (!isTag && !minLng && !minLat && !maxLng && !maxLat) {
      mapRef.current.setCenter([coordinates?.lon || 0, coordinates?.lat || 0]);
      const newZoom = searchInfoDetailData.destination.destinationType === DESTINATION_TYPE.GEO ? 8 : 13;
      mapRef.current.setZoom(newZoom);
      return;
    }
  }

  const { minLng, minLat, maxLng, maxLat } = outputCoordinates;

  if (
    !dealCards ||
    reloadType === ReloadType.SEARCH ||
    reloadType === ReloadType.FILTER ||
    reloadType === ReloadType.PAGING
  ) {
    if (!isMapSearch) {
      try {
        mapRef.current.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          {
            padding: 50,
            duration: 0,
            offset: [0, 0],
            maxZoom: 18,
          }
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
      if (isMobile) {
        adjustMapPositionUp(mapRef.current);
      }
    }
  }

  mapRef?.current?.resize();
};

export default handleMapLoad;
